import { useLocation } from '@gatsbyjs/reach-router';
import {
  Box,
  ClickAwayListener,
  FormHelperText,
  Typography,
} from '@mui/material';
import type { FormikContextType } from 'formik';
import { useFormikContext } from 'formik';
import React from 'react';

import {
  FormikCheckboxField,
  FormikDateField,
  FormikDropdownField,
  FormikFhirDateField,
  FormikTelField,
  FormikTextField,
} from '@components/FormikFields';
import InputLabel from '@components/InputLabel';

import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import { ClickableDiv } from './DrilldownItem.style';

export type ItemType =
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'dropdown'
  | 'number'
  | 'tel'
  | 'text-area'
  | 'text';

export type OutputType<
  T extends ItemType,
  IsFHIR extends boolean,
> = T extends 'date'
  ? IsFHIR extends true
    ? string | null
    : Date | null
  : T extends 'datetime'
    ? IsFHIR extends true
      ? string | null
      : Date | null
    : T extends 'dropdown'
      ? string
      : T extends 'text-area'
        ? string
        : T extends 'text'
          ? string
          : T extends 'tel'
            ? string
            : T extends 'number'
              ? number
              : null;

interface DrilldownItemProps<T extends ItemType, IsFHIR extends boolean> {
  label: string;
  name?: string;
  placeholder?: string;
  type?: ItemType;
  value?: boolean | number | string | null;
  readOnly?: boolean;
  required?: boolean;
  onChange?: (value: OutputType<T, IsFHIR>) => void;
  /** FHIR only */
  dateFormats?: DateFormat[];
  /** For dropdown fields: Options to select from */
  options?: {
    k: string;
    v: boolean | string;
  }[];
  /** Text area only; maximum # characters */
  charLimit?: number;
  /** For checkbox fields: Header text */
  headerLabel?: string;
  /** Is this drilldown item for a FHIR resource? */
  isFhir?: IsFHIR;
  /** Label color for dropdown input */
  labelColor?: string;
  isEditing?: boolean;
  max?: boolean;
  /** FHIR date fields only; earliest selectable date, as a FHIR date string */
  min?: string | null;
}

/**
 * Drilldown Item and form input field
 * Single text entry item that will not display if value is readonly or not in edit mode
 *
 * @description NC stands for Non-Curation, since Curation is the default export
 */
function DrilldownItem<T extends ItemType, IsFHIR extends boolean>({
  name = '',
  label = '',
  placeholder = '',
  type = 'text',
  value,
  readOnly = false,
  max = false,
  min,
  required = false,
  onChange,
  options,
  headerLabel = '',
  isFhir,
  dateFormats,
  labelColor = 'primary.main',
  charLimit,
  isEditing,
}: DrilldownItemProps<T, IsFHIR>) {
  if (!isFhir) {
    isFhir = true as IsFHIR;
  }

  const canEdit = !readOnly;

  const handleChange = React.useCallback(
    (value: any) => {
      onChange?.(value as unknown as OutputType<T, IsFHIR>);
    },
    [onChange]
  );

  const inputComponent = React.useMemo(() => {
    switch (type) {
      case 'checkbox':
        return (
          <FormikCheckboxField
            headerLabel={headerLabel}
            label={label}
            labelColor={labelColor}
            name={name}
            onChange={handleChange}
          />
        );
      case 'dropdown':
        return (
          <FormikDropdownField
            label={label}
            labelColor={labelColor}
            name={name}
            onChange={handleChange}
            options={options}
            required={required}
          />
        );
      case 'date':
      case 'datetime':
        return (
          <div>
            {isFhir ? (
              <FormikFhirDateField
                formats={dateFormats ?? []}
                label={label}
                name={name}
                onChange={handleChange}
                value={typeof value === 'string' ? value : null}
                max={max}
                min={min}
              />
            ) : (
              <FormikDateField
                label={label}
                name={name}
                onChange={handleChange}
              />
            )}
          </div>
        );
      case 'number':
        return (
          <FormikTextField
            autoComplete="off"
            label={label}
            name={name}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            type="number"
          />
        );
      case 'tel':
        return <FormikTelField label={label} name={name} required={required} />;
      case 'text':
      case 'text-area':
      default:
        return (
          <FormikTextField
            autoComplete="off"
            charLimit={charLimit}
            label={label}
            name={name}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            type={type === 'text-area' ? 'text-area' : 'text'}
          />
        );
    }
  }, [
    charLimit,
    dateFormats,
    handleChange,
    headerLabel,
    isFhir,
    label,
    labelColor,
    max,
    min,
    name,
    options,
    placeholder,
    required,
    type,
    value,
  ]);
  return (
    <Box sx={{ width: '100%' }}>
      {isEditing && canEdit ? (
        inputComponent
      ) : (
        <>
          <InputLabel label={label} style={{ color: labelColor }} />
          <Typography
            className="field-value"
            data-testid="drilldown-item-description"
            sx={{
              fontSize: '16px',
              width: '100%',
              wordBreak: 'break-word',
            }}
          >
            {typeof value !== 'undefined' && value !== null && value !== ''
              ? value
              : '-'}
          </Typography>
        </>
      )}
    </Box>
  );
}

function withCuration<T extends ItemType, IsFHIR extends boolean>(
  Component: React.ComponentType<DrilldownItemProps<T, IsFHIR>>
) {
  return (props: DrilldownItemProps<T, IsFHIR>) => {
    const { value, readOnly = false } = props;
    const {
      state: { isCurator, isPractitioner },
    } = useSession();
    const {
      state: { isCreating, isEditing },
    } = useCuration();

    // Show all fields for appointment, insurance, genetic info, family history and immunizations
    const { pathname } = useLocation();

    if (
      !value &&
      !(isCreating || isEditing) &&
      !(isCurator || isPractitioner) &&
      (!pathname.includes('appointments') ||
        !pathname.includes('family-history') ||
        !pathname.includes('genetic-information') ||
        !pathname.includes('immunizations'))
    ) {
      return null;
    }
    return (
      <Component
        {...props}
        readOnly={readOnly}
        isEditing={isEditing || isCreating}
      />
    );
  };
}

function withClickToEdit<T extends ItemType, IsFHIR extends boolean>(
  Component: React.ComponentType<DrilldownItemProps<T, IsFHIR>>
) {
  return (props: DrilldownItemProps<T, IsFHIR>) => {
    const { value, readOnly = false, name = '' } = props;

    const [isClickToEdit] = React.useState(true);
    const [originalValue, setOriginalValue] = React.useState(value);
    const [isEditing, setIsEditing] = React.useState(false);

    const canEdit = !readOnly;

    const context: FormikContextType<Record<string, unknown>> | undefined =
      useFormikContext<Record<string, unknown>>();
    const formik = React.useMemo(
      () => context as FormikContextType<Record<string, unknown>> | undefined,
      [context]
    );

    const handleStartEditing = () => {
      if (!isClickToEdit || !canEdit) {
        return;
      }
      setIsEditing(true);
    };

    const handleStopEditing = React.useCallback(() => {
      if (!isClickToEdit || !canEdit) {
        return;
      }
      setIsEditing(false);
    }, [canEdit, isClickToEdit]);

    const handleSave = React.useCallback(() => {
      if (!isClickToEdit || !canEdit) {
        return;
      }
      const v = formik?.values as any;
      if (name in v && v[name] !== originalValue) {
        setOriginalValue(v[name]);
      }
      handleStopEditing();
    }, [
      canEdit,
      handleStopEditing,
      isClickToEdit,
      name,
      originalValue,
      formik?.values,
    ]);

    const handleCancel = React.useCallback(() => {
      if (!isClickToEdit || !canEdit) {
        return;
      }
      handleStopEditing();
      if (
        originalValue !== undefined &&
        originalValue !== null &&
        name in (formik?.values as any) &&
        (formik?.values as any)[name] !== originalValue
      ) {
        formik?.setFieldValue(name, originalValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      canEdit,
      handleStopEditing,
      isClickToEdit,
      name,
      originalValue,
      formik?.setFieldValue,
      formik?.values,
    ]);

    React.useEffect(() => {
      if (!isEditing) {
        return;
      }
      const escapeListener = (e: KeyboardEvent) => {
        if (e.code === 'Escape') {
          handleCancel();
        }
      };
      const enterListener = (e: KeyboardEvent) => {
        if (e.code === 'Enter') {
          handleSave();
        }
      };
      document.addEventListener('keyup', escapeListener);
      document.addEventListener('keyup', enterListener);
      return () => {
        document.removeEventListener('keyup', escapeListener);
        document.removeEventListener('keyup', enterListener);
      };
    }, [handleCancel, handleSave, isEditing]);

    if (isEditing) {
      return (
        <ClickAwayListener onClickAway={handleStopEditing}>
          <Box>
            <Component {...props} readOnly={readOnly} isEditing={isEditing} />
          </Box>
        </ClickAwayListener>
      );
    }

    const error = formik?.errors[name];

    return (
      <ClickableDiv
        isClickable={isClickToEdit && canEdit}
        onClick={handleStartEditing}
        sx={{ mb: '10px' }}
      >
        <Component {...props} readOnly={readOnly} isEditing={isEditing} />
        {Boolean(error) && <FormHelperText>{error}</FormHelperText>}
      </ClickableDiv>
    );
  };
}

export { withClickToEdit, withCuration };
export default DrilldownItem;
